import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Plus, Trash2, Save, GripVertical, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Build / edit a saved Superset template.
 * Props:
 *   open, onOpenChange
 *   existing?: full superset doc when editing (else null)
 *   onSaved?: (savedDoc) => void
 */
export default function SupersetEditor({ open, onOpenChange, existing = null, onSaved }) {
  const isEdit = !!existing;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [restSeconds, setRestSeconds] = useState(90);
  const [items, setItems] = useState([]); // {exercise_id, name, muscle_groups, sets:[{reps,weight}], rest_seconds}
  const [library, setLibrary] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/exercises").then((r) => setLibrary(r.data));
    if (existing) {
      setName(existing.name || "");
      setDescription(existing.description || "");
      setRestSeconds(existing.rest_seconds ?? 90);
      setItems(JSON.parse(JSON.stringify(existing.exercises || [])));
    } else {
      setName(""); setDescription(""); setRestSeconds(90); setItems([]);
    }
    setSearch("");
  }, [open, existing]);

  const filteredLib = useMemo(
    () => library.filter((x) => !search || x.name.toLowerCase().includes(search.toLowerCase())),
    [library, search]
  );

  const addExercise = (ex) => {
    setItems((p) => [...p, {
      exercise_id: ex.exercise_id,
      name: ex.name,
      muscle_groups: ex.muscle_groups || [],
      sets: [{ reps: 10, weight: 0 }],
      rest_seconds: 0,
    }]);
  };
  const remove = (i) => setItems((p) => p.filter((_, k) => k !== i));
  const move = (i, dir) => setItems((p) => {
    const j = i + dir;
    if (j < 0 || j >= p.length) return p;
    const next = [...p];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const updSets = (i, sets) => setItems((p) => p.map((e, k) => k === i ? { ...e, sets } : e));
  const setField = (i, si, field, val) => updSets(i, items[i].sets.map((s, k) => k === si ? { ...s, [field]: val } : s));
  const addSet = (i) => updSets(i, [...items[i].sets, { reps: 10, weight: 0 }]);
  const removeSet = (i, si) => updSets(i, items[i].sets.filter((_, k) => k !== si));

  const save = async () => {
    if (!name.trim()) { toast.error("Give your superset a name"); return; }
    if (items.length < 2) { toast.error("A superset needs at least 2 exercises"); return; }
    setBusy(true);
    try {
      const body = { name: name.trim(), description, rest_seconds: restSeconds, exercises: items };
      const res = isEdit
        ? await api.put(`/supersets/${existing.superset_id}`, body)
        : await api.post("/supersets", body);
      toast.success(isEdit ? "Superset updated" : "Superset saved");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="superset-editor-title" className="font-display text-2xl uppercase">
            {isEdit ? "Edit Superset" : "New Superset"}
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-400">
            Combine 2 or more exercises into one back-to-back block. Save it once, drop it into any workout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs tracking-widest uppercase text-neutral-400">Superset Name</Label>
            <Input data-testid="ss-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Push Power" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-10" />
          </div>
          <div className="grid sm:grid-cols-[1fr_140px] gap-3">
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-400">Description</Label>
              <Textarea data-testid="ss-desc-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Quick note (optional)" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-400">Rest after round</Label>
              <Input data-testid="ss-rest-input" type="number" min={0} value={restSeconds} onChange={(e) => setRestSeconds(Number(e.target.value))} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-10" />
              <div className="text-[10px] text-neutral-500 mt-1 uppercase tracking-wider">seconds</div>
            </div>
          </div>
        </div>

        <div className="mt-4 border-t border-neutral-800 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-lg uppercase">Exercises in this Superset ({items.length})</h3>
          </div>
          {items.length === 0 && (
            <div className="border border-dashed border-neutral-800 p-4 text-center text-xs text-neutral-500 mb-3">
              Pick 2 or more exercises from the library below.
            </div>
          )}
          <div className="space-y-2 mb-4">
            {items.map((ex, i) => (
              <div key={i} data-testid={`ss-item-${i}`} className="border border-blue-500/40 bg-blue-500/5 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex flex-col">
                    <button data-testid={`ss-up-${i}`} onClick={() => move(i, -1)} className="text-neutral-500 hover:text-blue-500 text-[10px] leading-none px-1">▲</button>
                    <button data-testid={`ss-down-${i}`} onClick={() => move(i, 1)} className="text-neutral-500 hover:text-blue-500 text-[10px] leading-none px-1">▼</button>
                  </div>
                  <GripVertical className="w-3.5 h-3.5 text-neutral-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base uppercase truncate">{i + 1}. {ex.name}</div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{(ex.muscle_groups || []).join(", ")}</div>
                  </div>
                  <Button data-testid={`ss-remove-${i}`} size="icon" variant="ghost" onClick={() => remove(i)} className="text-neutral-400 hover:text-blue-500 h-7 w-7"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
                <div className="grid grid-cols-[40px_1fr_1fr_28px] gap-2 text-[10px] tracking-widest uppercase text-neutral-500 mb-1">
                  <div>Set</div><div>Reps</div><div>Weight</div><div></div>
                </div>
                {ex.sets.map((s, si) => (
                  <div key={si} className="grid grid-cols-[40px_1fr_1fr_28px] gap-2 mb-1 items-center">
                    <div className="text-neutral-400 text-xs pl-1">{si + 1}</div>
                    <Input data-testid={`ss-${i}-set-${si}-reps`} type="number" min={0} value={s.reps} onChange={(e) => setField(i, si, "reps", Number(e.target.value))} className="bg-neutral-900 border-neutral-800 rounded-none h-8 text-white text-sm" />
                    <Input data-testid={`ss-${i}-set-${si}-weight`} type="number" min={0} step={0.5} value={s.weight} onChange={(e) => setField(i, si, "weight", Number(e.target.value))} className="bg-neutral-900 border-neutral-800 rounded-none h-8 text-white text-sm" />
                    <Button size="icon" variant="ghost" onClick={() => removeSet(i, si)} className="text-neutral-500 hover:text-blue-500 h-6 w-6"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                <Button data-testid={`ss-add-set-${i}`} size="sm" variant="outline" onClick={() => addSet(i)} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider mt-1 h-7 text-[11px]">
                  <Plus className="w-3 h-3 mr-1" /> Set
                </Button>
              </div>
            ))}
          </div>

          <div className="border border-neutral-800 p-3">
            <div className="text-xs tracking-widest uppercase text-neutral-500 mb-2">Add from library</div>
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 text-neutral-500 absolute top-2.5 left-2.5" />
              <Input data-testid="ss-lib-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-8 bg-neutral-900 border-neutral-800 rounded-none h-9 text-white" />
            </div>
            <div className="divide-y divide-neutral-800 border border-neutral-800 max-h-48 overflow-y-auto">
              {filteredLib.slice(0, 80).map((ex) => (
                <button key={ex.exercise_id} data-testid={`ss-lib-${ex.exercise_id}`} onClick={() => addExercise(ex)} className="w-full flex items-center justify-between p-2.5 hover:bg-neutral-900 text-left">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{ex.name}</div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{ex.category} · {(ex.muscle_groups || []).join(", ")}</div>
                  </div>
                  <Plus className="w-4 h-4 text-blue-500 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="ss-cancel-btn" variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-10">Cancel</Button>
          <Button data-testid="ss-save-btn" onClick={save} disabled={busy} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
            <Save className="w-4 h-4 mr-2" /> {isEdit ? "Update" : "Save Superset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
